/**
 * API Client for Foodaholix Landing
 * 
 * A lightweight fetch-based API client following Gatsby best practices.
 * Uses GATSBY_ prefixed env variables which are exposed to the browser.
 */

// Get API base URL from environment variable
const API_BASE_URL = process.env.GATSBY_API_BASE_URL || 'https://apis-v2.foodaholix.com/api';

/**
 * Custom API Error class for better error handling
 */
class ApiError extends Error {
    constructor(message, status, data = null) {
        super(message);
        this.name = 'ApiError';
        this.status = status;
        this.data = data;
    }
}

/**
 * Default request configuration
 */
const defaultConfig = {
    headers: {
        'Content-Type': 'application/json',
    },
};

/**
 * Build URL with query parameters
 * @param {string} endpoint - API endpoint
 * @param {Object} params - Query parameters
 * @returns {string} Full URL with query string
 */
const buildUrl = (endpoint, params = {}) => {
    const url = new URL(`${API_BASE_URL}${endpoint}`);

    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
            url.searchParams.append(key, value);
        }
    });

    return url.toString();
};

/**
 * Parse response based on content type
 * @param {Response} response - Fetch response object
 * @returns {Promise<any>} Parsed response data
 */
const parseResponse = async (response) => {
    const contentType = response.headers.get('content-type');

    if (contentType && contentType.includes('application/json')) {
        return response.json();
    }

    return response.text();
};

/**
 * Main fetch wrapper with error handling
 * @param {string} endpoint - API endpoint (e.g., '/users')
 * @param {Object} options - Fetch options
 * @param {Object} options.params - Query parameters
 * @param {Object} options.body - Request body (will be stringified)
 * @param {Object} options.headers - Additional headers
 * @param {string} options.method - HTTP method (GET, POST, PUT, DELETE, PATCH)
 * @param {AbortSignal} options.signal - AbortController signal for cancellation
 * @returns {Promise<any>} Response data
 * @throws {ApiError} When request fails
 */
const apiFetch = async (endpoint, options = {}) => {
    const {
        params,
        body,
        headers = {},
        method = 'GET',
        signal,
        ...restOptions
    } = options;

    const url = buildUrl(endpoint, params);

    const config = {
        ...defaultConfig,
        ...restOptions,
        method,
        headers: {
            ...defaultConfig.headers,
            ...headers,
        },
    };

    // Add body for non-GET requests
    if (body && method !== 'GET') {
        config.body = JSON.stringify(body);
    }

    // Add abort signal if provided
    if (signal) {
        config.signal = signal;
    }

    try {
        const response = await fetch(url, config);
        const data = await parseResponse(response);

        if (!response.ok) {
            throw new ApiError(
                data?.message || `Request failed with status ${response.status}`,
                response.status,
                data
            );
        }

        return data;
    } catch (error) {
        // Re-throw ApiError as is
        if (error instanceof ApiError) {
            throw error;
        }

        // Handle abort errors
        if (error.name === 'AbortError') {
            throw new ApiError('Request was cancelled', 0, null);
        }

        // Handle network errors
        throw new ApiError(
            error.message || 'Network error occurred',
            0,
            null
        );
    }
};

/**
 * API Client with convenience methods
 */
const apiClient = {
    /**
     * GET request
     * @param {string} endpoint - API endpoint
     * @param {Object} params - Query parameters
     * @param {Object} options - Additional fetch options
     */
    get: (endpoint, params = {}, options = {}) =>
        apiFetch(endpoint, { ...options, params, method: 'GET' }),

    /**
     * POST request
     * @param {string} endpoint - API endpoint
     * @param {Object} body - Request body
     * @param {Object} options - Additional fetch options
     */
    post: (endpoint, body = {}, options = {}) =>
        apiFetch(endpoint, { ...options, body, method: 'POST' }),

    /**
     * PUT request
     * @param {string} endpoint - API endpoint
     * @param {Object} body - Request body
     * @param {Object} options - Additional fetch options
     */
    put: (endpoint, body = {}, options = {}) =>
        apiFetch(endpoint, { ...options, body, method: 'PUT' }),

    /**
     * PATCH request
     * @param {string} endpoint - API endpoint
     * @param {Object} body - Request body
     * @param {Object} options - Additional fetch options
     */
    patch: (endpoint, body = {}, options = {}) =>
        apiFetch(endpoint, { ...options, body, method: 'PATCH' }),

    /**
     * DELETE request
     * @param {string} endpoint - API endpoint
     * @param {Object} options - Additional fetch options
     */
    delete: (endpoint, options = {}) =>
        apiFetch(endpoint, { ...options, method: 'DELETE' }),
};

/**
 * Pre-defined API endpoints for type safety and consistency
 */
export const api = {
    /**
     * User Badges API
     */
    userBadges: {
        /**
         * Get leaderboard data
         * @param {number} limit - Number of users to fetch
         * @param {Object} options - Additional fetch options (e.g., signal for AbortController)
         * @returns {Promise<{users: Array}>}
         */
        getLeaderboard: (limit = 10, options = {}) =>
            apiClient.get('/userBadges/leaderboard', { limit }, options),
    },

    /**
     * Badges API
     */
    badges: {
        /**
         * Get all badges
         * @param {Object} options - Additional fetch options (e.g., signal for AbortController)
         * @returns {Promise<{badges: Array}>}
         */
        getAll: (options = {}) =>
            apiClient.get('/badges/all', {}, options),
    },

    /**
     * Founder Circles API
     */
    founderCircles: {
        /**
         * Get leaderboard data for a founder circle
         * @param {string} id - Founder circle short ID
         * @param {Object} options - Additional fetch options (e.g., signal for AbortController)
         * @returns {Promise<{members: Array, message: string}>}
         */
        getLeaderboard: (id, options = {}) =>
            apiClient.get(`/founder-circles/leaderboard/${id}`, {}, options),
    },
};

// Export utilities for custom use cases
export { apiClient, apiFetch, ApiError, API_BASE_URL };

export default api;
